import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Phone, Trash2, Edit, Save, X, GraduationCap, Briefcase, Award, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { agentApi } from '@/services/agentApi';

export const StudentProfileDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<any>({});

  useEffect(() => {
    if (id) {
      loadProfile();
    }
  }, [id]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await agentApi.getStudentProfile(parseInt(id!, 10));
      setProfile(data);
      setFormData(data);
    } catch (error: any) {
      console.error('Error loading profile:', error);
      toast.error(error?.response?.data?.error || 'Failed to load profile');
      navigate('/student-profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this student profile?')) {
      try {
        await agentApi.deleteStudentProfile(parseInt(id!, 10));
        toast.success('Profile deleted successfully');
        navigate('/student-profiles');
      } catch (error: any) {
        console.error('Error deleting profile:', error);
        toast.error(error?.response?.data?.error || 'Failed to delete profile');
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setFormData({ ...profile });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({ ...profile });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert date strings to Date objects for fields that need it
      const updateData = { ...formData };
      
      const dateFields = [
        'dateOfBirth', 'passportExpiryDate', 'graduationDate',
        'englishExamDate', 'greExamDate', 'gmatExamDate', 'satExamDate',
        'preferredStartDate', 'programmeStartDate', 'programmeEndDate'
      ];

      dateFields.forEach(field => {
        if (updateData[field] && updateData[field] !== '') {
          updateData[field] = new Date(updateData[field]);
        } else if (updateData[field] === '') {
          updateData[field] = null;
        }
      });

      await agentApi.updateStudentProfile(parseInt(id!, 10), updateData);
      toast.success('Profile updated successfully');
      setIsEditing(false);
      loadProfile(); // Reload to get updated data
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error(error?.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString();
  };

  const formatDateInput = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const renderField = (label: string, field: string, type: string = 'text', options?: string[]) => {
    const value = isEditing ? formData[field] : profile[field];
    
    if (isEditing) {
      if (type === 'textarea') {
        return (
          <div className="mb-3">
            <label className="form-label small fw-semibold">{label}</label>
            <textarea
              className="form-control form-control-sm"
              value={value || ''}
              onChange={(e) => handleChange(field, e.target.value)}
              rows={3}
            />
          </div>
        );
      } else if (type === 'checkbox') {
        return (
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleChange(field, e.target.checked)}
              id={`edit-${field}`}
            />
            <label className="form-check-label small" htmlFor={`edit-${field}`}>
              {label}
            </label>
          </div>
        );
      } else if (type === 'date') {
        return (
          <div className="mb-3">
            <label className="form-label small fw-semibold">{label}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={formatDateInput(value)}
              onChange={(e) => handleChange(field, e.target.value)}
            />
          </div>
        );
      } else if (type === 'select' && options) {
        return (
          <div className="mb-3">
            <label className="form-label small fw-semibold">{label}</label>
            <select
              className="form-select form-select-sm"
              value={value || ''}
              onChange={(e) => handleChange(field, e.target.value)}
            >
              <option value="">Select...</option>
              {options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );
      } else {
        return (
          <div className="mb-3">
            <label className="form-label small fw-semibold">{label}</label>
            <input
              type={type}
              className="form-control form-control-sm"
              value={value || ''}
              onChange={(e) => handleChange(field, e.target.value)}
            />
          </div>
        );
      }
    } else {
      if (value !== null && value !== undefined && value !== '') {
        return (
          <div className="mb-3">
            <label className="text-muted small">{label}</label>
            <div>{type === 'date' ? formatDate(value) : String(value)}</div>
          </div>
        );
      }
      return null;
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center mb-4">
            <button
              className="btn btn-link p-0 me-2"
              onClick={() => navigate('/student-profiles')}
            >
              <ArrowLeft size={18} />
            </button>
            <div className="flex-grow-1">
              <h1 className="h4 fw-bold text-dark mb-0">Student Profile Details</h1>
            </div>
            <div className="d-flex gap-2">
              {isEditing ? (
                <>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    <X size={16} className="me-2" />
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    <Save size={16} className="me-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleEdit}
                  >
                    <Edit size={16} className="me-2" />
                    Edit Profile
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleDelete}
                  >
                    <Trash2 size={16} className="me-2" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="row g-4">
            {/* A. QUALIFICATION */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0 fw-semibold">
                    <GraduationCap size={18} className="me-2" />
                    A. Qualification Information
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      {renderField('Programme Strand', 'programmeStrand')}
                      {renderField('Programme Start Date', 'programmeStartDate', 'date')}
                      {renderField('Programme End Date', 'programmeEndDate', 'date')}
                      {renderField('Studied at Kalandra Before', 'studiedAtKalandraBefore', 'checkbox')}
                    </div>
                    <div className="col-md-6">
                      {renderField('How Did You Find Out', 'howDidYouFindOut', 'textarea')}
                      {renderField('When did you study at the institution', 'howDidYouFindOutWhen')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* B. PERSONAL DETAILS */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">
                    <User size={18} className="me-2" />
                    B. Personal Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-6">
                      {renderField('Preferred Title', 'preferredTitle', 'select', ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof'])}
                      {renderField('First Name', 'firstName')}
                      {renderField('Last Name', 'lastName')}
                      {renderField('Middle Name', 'middleName')}
                    </div>
                    <div className="col-6">
                      {renderField('Date of Birth', 'dateOfBirth', 'date')}
                      {renderField('Gender', 'gender', 'select', ['Male', 'Female', 'Other', 'Prefer not to say'])}
                      {renderField('NSN', 'nsn')}
                      {renderField('Country of Citizenship', 'countryOfCitizenship')}
                    </div>
                    <div className="col-12">
                      {renderField('Email', 'email', 'email')}
                      {renderField('Ethnicity', 'ethnicity')}
                      {renderField('Passport Number', 'passportNumber')}
                      {renderField('Passport Expiry Date', 'passportExpiryDate', 'date')}
                      {renderField('Iwi', 'iwi')}
                      {renderField('Prior Activity', 'priorActivity', 'textarea')}
                      {renderField('First Language', 'firstLanguage')}
                      {renderField('Marital Status', 'maritalStatus', 'select', ['Single', 'Married', 'Divorced', 'Widowed', 'Other'])}
                      {renderField('Disability', 'disability', 'checkbox')}
                      {renderField('Disability Details', 'disabilityDetails', 'textarea')}
                      {renderField('Conviction Checks', 'convictionChecks', 'checkbox')}
                      {renderField('Conviction Details', 'convictionDetails', 'textarea')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* C. CONTACT DETAILS */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">
                    <Phone size={18} className="me-2" />
                    C. Contact Details
                  </h5>
                </div>
                <div className="card-body">
                  <h6 className="small fw-semibold text-muted mb-2">Home Country Contact</h6>
                  {renderField('Home Address', 'homeAddress', 'textarea')}
                  {renderField('Home Phone', 'homePhone')}
                  {renderField('Home Mobile', 'homeMobile')}
                  {renderField('Home Fax', 'homeFax')}
                  {renderField('Home Accommodation Type', 'homeAccommodationType')}

                  <h6 className="small fw-semibold text-muted mb-2 mt-3">Study Location Contact</h6>
                  {renderField('Study Address', 'studyAddress', 'textarea')}
                  {renderField('Study Phone', 'studyPhone')}
                  {renderField('Study Mobile', 'studyMobile')}
                  {renderField('Study Fax', 'studyFax')}
                  {renderField('Study Accommodation Type', 'studyAccommodationType')}

                  <h6 className="small fw-semibold text-muted mb-2 mt-3">Next of Kin</h6>
                  {renderField('Next of Kin Name', 'nextOfKinName')}
                  {renderField('Next of Kin Phone', 'nextOfKinPhone')}
                  {renderField('Next of Kin Mobile', 'nextOfKinMobile')}
                  {renderField('Next of Kin Fax', 'nextOfKinFax')}
                  {renderField('Next of Kin Email', 'nextOfKinEmail', 'email')}
                  {renderField('Next of Kin Address', 'nextOfKinAddress', 'textarea')}

                  <h6 className="small fw-semibold text-muted mb-2 mt-3">Emergency Contact</h6>
                  {renderField('Emergency Contact Name', 'emergencyContactName')}
                  {renderField('Emergency Contact Phone', 'emergencyContactPhone')}
                  {renderField('Emergency Contact Mobile', 'emergencyContactMobile')}
                  {renderField('Emergency Contact Email', 'emergencyContactEmail', 'email')}
                  {renderField('Emergency Contact Address', 'emergencyContactAddress', 'textarea')}
                </div>
              </div>
            </div>

            {/* D. ACADEMIC INFORMATION */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">
                    <GraduationCap size={18} className="me-2" />
                    D. Academic Information
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="small fw-semibold text-muted mb-2">Secondary School</h6>
                        {renderField('Secondary School Location', 'secondarySchoolLocation')}
                      {renderField('Secondary School Name', 'secondarySchoolName')}
                      {renderField('Secondary School Last Year', 'secondarySchoolLastYear')}
                      {renderField('Secondary School Highest Level', 'secondarySchoolHighestLevel')}
                    </div>
                    <div className="col-md-6">
                      <h6 className="small fw-semibold text-muted mb-2">Tertiary Education</h6>
                      {renderField('First Year Tertiary Enrolment', 'firstYearTertiaryEnrolment', 'checkbox')}
                      {renderField('Tertiary Institution Name', 'tertiaryInstitutionName')}
                      {renderField('Tertiary Institution Year', 'tertiaryInstitutionYear')}
                    </div>
                    <div className="col-md-6">
                      <h6 className="small fw-semibold text-muted mb-2 mt-3">English Proficiency</h6>
                      {renderField('English Level', 'englishLevel', 'select', ['BEGINNER', 'ELEMENTARY', 'INTERMEDIATE', 'ADVANCED'])}
                      {renderField('English Exam Type', 'englishExamType', 'select', ['IELTS', 'TOEFL', 'PTE', 'Cambridge', 'Other'])}
                      {renderField('English Exam Date', 'englishExamDate', 'date')}
                      {renderField('Has English Scores', 'hasEnglishScores', 'checkbox')}
                      {renderField('English Overall Score', 'englishOverallScore')}
                      {renderField('English Listening Score', 'englishListeningScore')}
                      {renderField('English Reading Score', 'englishReadingScore')}
                      {renderField('English Writing Score', 'englishWritingScore')}
                      {renderField('English Speaking Score', 'englishSpeakingScore')}
                    </div>
                    <div className="col-md-6">
                      <h6 className="small fw-semibold text-muted mb-2 mt-3">Career</h6>
                      {renderField('Career Intentions', 'careerIntentions', 'textarea')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* E. WORK HISTORY */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">
                    <Briefcase size={18} className="me-2" />
                    E. Work History
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      {renderField('Worked as Health Care Assistant', 'workedAsHealthCareAssistant', 'checkbox')}
                      {renderField('Health Care Assistant Duration', 'healthCareAssistantDuration')}
                      {renderField('Other Healthcare Qualifications', 'otherHealthcareQualifications', 'checkbox')}
                      {renderField('Healthcare Qualifications Details', 'healthcareQualificationsDetails', 'textarea')}
                      {renderField('Continue Studies or Workforce', 'continueStudiesOrWorkforce')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">Additional Information</h5>
                </div>
                <div className="card-body">
                  {renderField('Why Do You Want To Do Programme', 'whyDoYouWantToDoProgramme', 'textarea')}
                </div>
              </div>
            </div>

            {/* LEGACY FIELDS */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-warning text-dark">
                  <h5 className="mb-0 fw-semibold">Legacy Fields</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="small fw-semibold text-muted mb-2">Contact (Legacy)</h6>
                      {renderField('Phone Number', 'phoneNumber')}
                      {renderField('Address', 'address', 'textarea')}
                      {renderField('City', 'city')}
                      {renderField('Province', 'province')}
                      {renderField('Country', 'country')}
                      {renderField('Postal Code', 'postalCode')}
                    </div>
                    <div className="col-md-6">
                      <h6 className="small fw-semibold text-muted mb-2">Education (Legacy)</h6>
                      {renderField('Current Education', 'currentEducation', 'textarea')}
                      {renderField('Level of Education', 'levelOfEducation')}
                      {renderField('Institution Name', 'institutionName')}
                      {renderField('Institution Country', 'institutionCountry')}
                      {renderField('Graduation Date', 'graduationDate', 'date')}
                      {renderField('Degree Name', 'degreeName')}
                      {renderField('GPA', 'gpa')}
                      {renderField('Grading Scheme', 'gradingScheme')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* TEST SCORES */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">
                    <Award size={18} className="me-2" />
                    Test Scores
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <h6 className="small fw-semibold text-muted mb-2">GRE Scores</h6>
                      {renderField('Has GRE Scores', 'hasGREScores', 'checkbox')}
                      {renderField('GRE Exam Date', 'greExamDate', 'date')}
                      {renderField('GRE Overall Score', 'greOverallScore')}
                      {renderField('GRE Verbal Score', 'greVerbalScore')}
                      {renderField('GRE Quantitative Score', 'greQuantitativeScore')}
                      {renderField('GRE Analytical Score', 'greAnalyticalScore')}
                    </div>
                    <div className="col-md-4">
                      <h6 className="small fw-semibold text-muted mb-2">GMAT Scores</h6>
                      {renderField('Has GMAT Scores', 'hasGMATScores', 'checkbox')}
                      {renderField('GMAT Exam Date', 'gmatExamDate', 'date')}
                      {renderField('GMAT Total Score', 'gmatTotalScore')}
                    </div>
                    <div className="col-md-4">
                      <h6 className="small fw-semibold text-muted mb-2">SAT Scores</h6>
                      {renderField('Has SAT Scores', 'hasSATScores', 'checkbox')}
                      {renderField('SAT Exam Date', 'satExamDate', 'date')}
                      {renderField('SAT Overall Score', 'satOverallScore')}
                      {renderField('SAT Math Score', 'satMathScore')}
                      {renderField('SAT Reading Score', 'satReadingScore')}
                      {renderField('SAT Writing Score', 'satWritingScore')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PREFERENCES */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0 fw-semibold">
                    <Globe size={18} className="me-2" />
                    Preferences
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      {renderField('Course of Interest', 'courseOfInterest', 'textarea')}
                      {renderField('Country of Choice', 'countryOfChoice')}
                      {renderField('Budget', 'budget')}
                      {renderField('Preferred Start Date', 'preferredStartDate', 'date')}
                      {renderField('Study Mode', 'studyMode')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-6">
                      <label className="text-muted small">Created</label>
                      <div>{formatDate(profile.createdAt)}</div>
                    </div>
                    <div className="col-6">
                      <label className="text-muted small">Last Updated</label>
                      <div>{formatDate(profile.updatedAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
